import css from './AdminDataTable.module.css';
import { useEffect, useState } from 'react';
import { DataError } from '../../../types/appTypes';
import Spinner from '../../Common/Spinner/Spinner';
import { CountList } from '../../../types/appModels';
import { DebounceInput } from 'react-debounce-input';
import { 
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiSlash,
  FiEye,
  FiMail,
  FiFilter,
  FiPlus,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

// Reusing existing types and enums
type ActionHandler<R extends DataTableRow> = (action: string, row: R) => void;
export type FilterHandler = (filters: FilterColumn[]) => void;
type ValueRender<R extends DataTableRow> = (row: R, handleAction: ActionHandler<R>) => JSX.Element;
type FilterRender<R extends DataTableRow> = (column: DataColumn<R>, handleFilter: FilterHandler, activeFilters?: FilterColumn[]) => JSX.Element;
type IsActionable<R extends DataTableRow> = (row: R) => boolean;

export type ActionDefinition<R extends DataTableRow> = {
  action: string;
  label: string;
  allowed?: IsActionable<R>;
  icon?: React.ReactNode; // New property for custom icon
};

// Existing enums
export enum SortDirection {
  None,
  Ascending,
  Descending,
}

export enum DisplayAlignment {
  Left,
  Center,
  Right,
}

export enum TableStatus {
  Idle,
  Loading,
  Acting,
  Sorting,
  Filtering,
  Success,
  Fail,
  Error,
}

// sort
export type SortColumn = {
  columnId: string;
  direction: SortDirection;
};

// filter
export type FilterOption = {
  label: string;
  value: string;
};

export type FilterColumn = {
  columnId: string;
  filter?: string;
};

interface DataFilterProps<R extends DataTableRow> {
  column: DataColumn<R>;
  handleFilter: FilterHandler;
  activeFilters?: FilterColumn[];
  defaultFilter?: string;
}

interface DateDataFilterProps<R extends DataTableRow> {
  column: DataColumn<R>;
  handleFilter: FilterHandler;
  defaultFromFilter?: string | number;
  defaultToFilter?: string | number;
}

const TextDataFilter = <R extends DataTableRow>({ column, handleFilter, activeFilters }: DataFilterProps<R>) => {
  const [filter, setFilter] = useState<string>('');

  useEffect(() => {
    const activeFilter = activeFilters?.find((f: FilterColumn) => f.columnId === column.id);
    if (!activeFilter) {
      setFilter('');
    }
  }, [activeFilters, column.id]);

  const handleChange = (value: string) => {
    setFilter(value);
    if (value) {
      handleFilter([{ columnId: column.id, filter: value }]);
    } else {
      handleFilter([]);
    }
  };

  return (
    <DebounceInput
      className={css.textFilter}
      value={filter}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={`Filter by ${column.title.toLowerCase()}`}
      debounceTimeout={300}
    />
  );
};

// Improved date range filter
export const DateRangeFilter = <R extends DataTableRow>({
  column,
  handleFilter,
  defaultFromFilter,
  defaultToFilter,
}: DateDataFilterProps<R>) => {
  const [filterState, setFilterState] = useState({
    from: defaultFromFilter,
    to: defaultToFilter,
  });

  useEffect(() => {
    let setFrom = false;
    let setTo = false;
    let newFromFilter = defaultFromFilter;
    let newToFilter = defaultToFilter;

    if (newFromFilter !== undefined && typeof newFromFilter === 'number') {
      const filtFromDate = new Date(newFromFilter);
      const year = filtFromDate.getFullYear();
      const month = (filtFromDate.getMonth() + 1).toString().padStart(2, '0');
      const day = filtFromDate.getDate().toString().padStart(2, '0');
      const hours = filtFromDate.getHours().toString().padStart(2, '0');
      const mins = filtFromDate.getMinutes().toString().padStart(2, '0');
      newFromFilter = `${year}-${month}-${day}T${hours}:${mins}`;
      setFrom = true;
    }

    if (newToFilter !== undefined && typeof newToFilter === 'number') {
      const filtToDate = new Date(newToFilter);
      const year = filtToDate.getFullYear();
      const month = (filtToDate.getMonth() + 1).toString().padStart(2, '0');
      const day = filtToDate.getDate().toString().padStart(2, '0');
      const hours = filtToDate.getHours().toString().padStart(2, '0');
      const mins = filtToDate.getMinutes().toString().padStart(2, '0');
      newToFilter = `${year}-${month}-${day}T${hours}:${mins}`;
      setTo = true;
    }

    if (setFrom || setTo) {
      setFilterState((pv) => {
        return { ...pv, from: newFromFilter, to: newToFilter };
      });
    }

    if (newFromFilter !== undefined || newToFilter !== undefined) {
      const filters: FilterColumn[] = [];

      if (newFromFilter !== undefined) {
        filters.push({
          columnId: `${column.id}From`,
          filter: newFromFilter.toString(),
        });
      }

      if (newToFilter !== undefined) {
        filters.push({
          columnId: `${column.id}To`,
          filter: newToFilter.toString(),
        });
      }

      handleFilter(filters);
    }
  }, []);

  return (
    <div className={css.dateRange}>
      <div className={css.dateInput}>
        <input
          type='datetime-local'
          className={css.dateFilter}
          value={filterState.from}
          onChange={(e) => {
            const newVal = e.target.value === '' ? undefined : e.target.value;
            const fc: FilterColumn = {
              columnId: `${column.id}From`,
              filter: newVal,
            };
            setFilterState((pv) => {
              return { ...pv, from: newVal };
            });
            handleFilter([fc]);
          }}
        />
      </div>
      <div className={css.dateInput}>
        <input
          type='datetime-local'
          className={css.dateFilter}
          value={filterState.to}
          onChange={(e) => {
            const newVal = e.target.value === '' ? undefined : e.target.value;
            const fc: FilterColumn = {
              columnId: `${column.id}To`,
              filter: newVal,
            };
            setFilterState((pv) => {
              return { ...pv, to: newVal };
            });
            handleFilter([fc]);
          }}
        />
      </div>
    </div>
  );
};

// Styled dropdown filter
export const OptionsDataFilter = <R extends DataTableRow>({
  column,
  options,
  handleFilter,
}: {
  column: DataColumn<R>;
  options: FilterOption[];
  handleFilter: FilterHandler;
}) => {
  return (
    <select
      className={css.selectFilter}
      onChange={(e) => {
        const fc: FilterColumn = {
          columnId: column.id,
          filter: e.target.value === '' ? undefined : e.target.value,
        };
        handleFilter([fc]);
      }}
    >
      <option value=''>All</option>
      {options.map((fo) => (
        <option key={fo.value} value={fo.value}>
          {fo.label}
        </option>
      ))}
    </select>
  );
};

// state
export type TableState<T> = {
  status: TableStatus;
  error?: DataError;
  data?: CountList<T>;
  sort?: SortColumn;
  filters?: FilterColumn[];
};

// row
export interface DataTableRow {
  [key: string]: string | number | boolean | Date | undefined;
  id: string;
}

// columns
export interface DataColumn<R extends DataTableRow> {
  id: string;
  title: string;
  align: DisplayAlignment;
  sortable: boolean;
  filterable: boolean;
  widthPercent?: number;
  renderFilter: FilterRender<R>;
  render: ValueRender<R>;
}

abstract class BaseColumn<R extends DataTableRow> implements DataColumn<R> {
  id: string;
  title: string;
  align: DisplayAlignment;
  sortable: boolean;
  filterable: boolean;
  widthPercent?: number;
  customRenderFilter?: FilterRender<R>;

  constructor(
    id: string,
    title: string,
    align: DisplayAlignment = DisplayAlignment.Left,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    customRenderFilter?: FilterRender<R>
  ) {
    this.id = id;
    this.title = title;
    this.align = align;
    this.sortable = sortable;
    this.filterable = filterable;
    this.widthPercent = widthPercent;
    this.customRenderFilter = customRenderFilter;
  }

  abstract render: ValueRender<R>;

  renderFilter: FilterRender<R> = (
    column: DataColumn<R>,
    handleFilter: FilterHandler,
    activeFilters?: FilterColumn[]
  ) => {
    if (!this.filterable) return <>&nbsp;</>;

    if (this.customRenderFilter === undefined)
      return <TextDataFilter column={column} handleFilter={handleFilter} activeFilters={activeFilters} />;

    return this.customRenderFilter(column, handleFilter, activeFilters);
  };
}

// Enhanced Actions Column with icons
export class ActionsColumn<R extends DataTableRow> extends BaseColumn<R> {
  actions: ActionDefinition<R>[];
  useIcons: boolean;

  constructor(
    id: string,
    title: string = 'Actions',
    actions: ActionDefinition<R>[],
    useIcons: boolean = true,
    widthPercent?: number
  ) {
    super(id, title, DisplayAlignment.Center, false, false, widthPercent);
    this.actions = actions;
    this.useIcons = useIcons;
  }

  render: ValueRender<R> = (row, handleAction) => {
    if (this.useIcons) {
      return (
        <div className={css.actionsContainer}>
          {this.actions.map((definition) => {
            if (definition.allowed && !definition.allowed(row)) {
              return null;
            }
            
            // Default icon mapping if not provided
            let icon = definition.icon;
            if (!icon) {
              switch (definition.action) {
                case 'MANAGE':
                case 'APPROVE':
                  icon = <FiEdit2 />;
                  break;
                case 'DELETE':
                  icon = <FiTrash2 />;
                  break;
                case 'ENABLE':
                  icon = <FiCheckCircle />;
                  break;
                case 'DISABLE':
                  icon = <FiSlash />;
                  break;
                case 'RESEND':
                  icon = <FiMail />;
                  break;
                default:
                  icon = <FiEye />;
              }
            }
            
            const actionClass = `${css.actionButton} ${css[definition.action.toLowerCase()]}`;
            
            return (
              <span 
                key={definition.action}
                className={actionClass}
                onClick={() => handleAction(definition.action, row)}
                title={definition.label}
              >
                {icon}
              </span>
            );
          })}
        </div>
      );
    } else {
      // Traditional text-based actions for backward compatibility
      const actions: JSX.Element[] = [];

      for (let definition of this.actions) {
        if (definition.allowed === undefined || definition.allowed(row)) {
          actions.push(
            <span className={css.action}>
              [
              <ClickWrapper
                action={definition.action}
                row={row}
                allowAction={definition.allowed}
                handleAction={handleAction}
              >
                {definition.label}
              </ClickWrapper>
              ]
            </span>
          );
        }
      }

      return <div className={css.actions}>{actions}</div>;
    }
  };
}

export class TextDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  action?: string;
  allowAction?: IsActionable<R>;
  customRender?: (value: any) => JSX.Element;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    customRender?: (value: any) => JSX.Element,
    action?: string,
    allowAction?: IsActionable<R>,
    customRenderFilter?: FilterRender<R>,
    align: DisplayAlignment = DisplayAlignment.Left
  ) {
    super(
      id,
      title,
      align,
      sortable,
      filterable,
      widthPercent,
      customRenderFilter
    );
    this.action = action;
    this.allowAction = allowAction;
    this.customRender = customRender;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null) {
      return <></>;
    }

    if (this.customRender) {
      return (
        <ClickWrapper
          action={this.action}
          row={row}
          allowAction={this.allowAction}
          handleAction={handleAction}
        >
          {this.customRender(v)}
        </ClickWrapper>
      );
    }

    return (
      <ClickWrapper
        action={this.action}
        row={row}
        allowAction={this.allowAction}
        handleAction={handleAction}
      >
        {v!.toString()}
      </ClickWrapper>
    );
  };
}

export class DateDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  formatter?: Intl.DateTimeFormat;
  action?: string;
  allowAction?: IsActionable<R>;
  customRender?: (value: Date) => JSX.Element;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    customRender?: (value: Date) => JSX.Element,
    formatter?: Intl.DateTimeFormat,
    action?: string,
    allowAction?: IsActionable<R>,
    customRenderFilter?: FilterRender<R>,
    align: DisplayAlignment = DisplayAlignment.Left
  ) {
    super(
      id,
      title,
      align,
      sortable,
      filterable,
      widthPercent,
      customRenderFilter
    );
    this.formatter = formatter;
    this.action = action;
    this.allowAction = allowAction;
    this.customRender = customRender;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null || !(v instanceof Date)) {
      return <></>;
    }

    if (this.customRender) {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          {this.customRender(v)}
        </ClickWrapper>
      );
    }

    if (this.formatter === undefined) {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          <span className={css.dateCell}>
            {new Intl.DateTimeFormat(navigator.language, {
              dateStyle: 'short',
              timeStyle: 'long',
              timeZone: 'UTC',
            }).format(v)}
          </span>
        </ClickWrapper>
      );
    }

    return (
      <ClickWrapper
        action={this.action}
        allowAction={this.allowAction}
        row={row}
        handleAction={handleAction}
      >
        <span className={css.dateCell}>{this.formatter.format(v)}</span>
      </ClickWrapper>
    );
  };
}

export class BooleanDataColumn<R extends DataTableRow> extends BaseColumn<R> {
  action?: string;
  allowAction?: IsActionable<R>;
  customRender?: (value: boolean) => JSX.Element;

  constructor(
    id: string,
    title: string,
    sortable: boolean = true,
    filterable: boolean = false,
    widthPercent?: number,
    customRender?: (value: boolean) => JSX.Element,
    action?: string,
    allowAction?: IsActionable<R>
  ) {
    super(
      id,
      title,
      DisplayAlignment.Center,
      sortable,
      filterable,
      widthPercent,
      (column, handleFilter, activeFilters) => {
        return (
          <OptionsDataFilter
            column={column}
            handleFilter={handleFilter}
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ]}
          />
        );
      }
    );
    this.action = action;
    this.allowAction = allowAction;
    this.customRender = customRender;
  }

  render: ValueRender<R> = (row, handleAction) => {
    const v = row[this.id];
    if (v === undefined || v === null) {
      return <></>;
    }

    const boolValue = Boolean(v);

    if (this.customRender) {
      return (
        <ClickWrapper
          action={this.action}
          allowAction={this.allowAction}
          row={row}
          handleAction={handleAction}
        >
          {this.customRender(boolValue)}
        </ClickWrapper>
      );
    }

    return (
      <ClickWrapper
        action={this.action}
        allowAction={this.allowAction}
        row={row}
        handleAction={handleAction}
      >
        <span className={`${css.booleanBadge} ${boolValue ? css.booleanTrue : css.booleanFalse}`}>
          {boolValue ? 'Yes' : 'No'}
        </span>
      </ClickWrapper>
    );
  };
}

// data table
type AdminDataTableProps<R extends DataTableRow, T> = {
  columns: readonly DataColumn<R>[];
  rows: readonly R[];
  total: number;
  allowFilter: boolean;
  allowNoSort: boolean;
  allowAdd?: boolean;
  addLabel?: string;
  onSort?: (sortColumn?: SortColumn) => void;
  onFilter?: (filterColumns: FilterColumn[]) => void;
  onLoadMore?: () => void;
  onAdd?: () => void;
  onAction?: (action: string, row: R) => void;
  state: TableState<T>;
  renderActionIcons?: (row: R) => JSX.Element; // Optional custom action renderer
};

export const AdminDataTable = <R extends DataTableRow, T>({
  columns,
  rows,
  total,
  allowFilter,
  allowNoSort,
  allowAdd,
  addLabel,
  onSort,
  onFilter,
  onLoadMore,
  onAdd,
  onAction,
  state,
  renderActionIcons,
}: AdminDataTableProps<R, T>) => {
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters = state.filters?.length || 0;

  const handleSortClick = (column: DataColumn<R>) => {
    if (onSort === undefined || !column.sortable) {
      return;
    }

    let newDirection = SortDirection.Ascending;
    if (state.sort !== undefined && state.sort.columnId === column.id) {
      if (state.sort.direction === SortDirection.Ascending) {
        newDirection = SortDirection.Descending;
      } else if (state.sort.direction === SortDirection.Descending) {
        newDirection = allowNoSort
          ? SortDirection.None
          : SortDirection.Ascending;
      }
    }

    let newSortColumn: SortColumn | undefined = undefined;
    if (newDirection !== SortDirection.None) {
      newSortColumn = { columnId: column.id, direction: newDirection };
    }

    onSort(newSortColumn);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    if (onFilter === undefined) {
      return;
    }

    onFilter(filters);
  };

  const handleActionClick = (action: string, row: R) => {
    if (onAction !== undefined) onAction(action, row);
  };

  // Render table header with actions
  const renderTableHeader = () => (
    <div className={css.tableHeader}>
      <div className={css.tableInfo}>
        {total !== undefined && (
          <span>{total} {total === 1 ? 'item' : 'items'}</span>
        )}
      </div>
      <div className={css.tableActions}>
        {allowFilter && (
          <button 
            className={`${css.filterButton} ${activeFilters > 0 ? css.activeFilter : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle filters"
          >
            <FiFilter /> 
            {activeFilters > 0 && <span className={css.filterCount}>{activeFilters}</span>}
          </button>
        )}
        {allowAdd && onAdd && (
          <button 
            className={css.addButton}
            onClick={onAdd}
            title={addLabel || 'Add'}
          >
            <FiPlus /> <span>{addLabel || 'Add'}</span>
          </button>
        )}
      </div>
    </div>
  );

  const buildTableHead = (): JSX.Element => {
    const headers: JSX.Element[] = [];
    const headerCells: JSX.Element[] = [];
    const filterCells: JSX.Element[] = [];
    const isFilterOn = allowFilter && showFilters && 
      onFilter !== undefined && columns.filter((c) => c.filterable).length > 0;

    columns.forEach((c) => {
      let sortIcon: JSX.Element = <></>;
      let isSorted = false;
      
      if (state.sort !== undefined && state.sort.columnId === c.id) {
        isSorted = true;
        sortIcon = state.sort.direction === SortDirection.Ascending ? 
          <FiArrowUp className={css.sortIcon} /> : 
          <FiArrowDown className={css.sortIcon} />;
      }

      let headerClass = css.tableHeader;
      if (onSort !== undefined && c.sortable) headerClass += ` ${css.sortable}`;
      
      // Add hideOnMobile class for specific columns
      if (c.id === 'createdDate' || c.id === 'lastLoginDate' || c.id === 'enabled' || c.id === 'role') {
        headerClass += ` ${css.hideOnMobile}`;
      }

      // Apply width percentage if specified
      const styleProps: React.CSSProperties = {};
      if (c.widthPercent !== undefined) {
        styleProps.width = `${c.widthPercent}%`;
      }

      // Cell alignment
      let cellClass = '';
      if (c.align === DisplayAlignment.Center) cellClass = css.center;
      if (c.align === DisplayAlignment.Right) cellClass = css.right;

      headerCells.push(
        <th 
          className={headerClass} 
          onClick={() => handleSortClick(c)}
          style={styleProps}
          key={c.id}
          data-column={c.id}
        >
          <div className={css.headerContent}>
            <span className={cellClass}>{c.title}</span>
            {isSorted && sortIcon}
          </div>
        </th>
      );

      if (isFilterOn) {
        const filterClass = c.id === 'createdDate' || c.id === 'lastLoginDate' || c.id === 'enabled' || c.id === 'role'
          ? `${css.filterCell} ${css.hideOnMobile}`
          : css.filterCell;

        const filterDisplay: JSX.Element = (
          <th className={filterClass} style={styleProps} key={`filter-${c.id}`}>
            {c.filterable ? c.renderFilter(c, handleFilter, state.filters) : null}
          </th>
        );
        filterCells.push(filterDisplay);
      }
    });
    
    headers.push(<tr key="header-row">{headerCells}</tr>);
    if (isFilterOn) {
      headers.push(<tr key="filter-row">{filterCells}</tr>);
    }

    return <thead>{headers}</thead>;
  };

  const buildTableBody = (): JSX.Element => {
    const body: JSX.Element[] = [];

    if (
      state.status === TableStatus.Acting ||
      state.status === TableStatus.Loading ||
      state.status === TableStatus.Sorting ||
      state.status === TableStatus.Filtering
    ) {
      body.push(
        <tr key="loading-row">
          <td colSpan={columns.length} className={css.loading}>
            <div className={css.spinner}>
              <Spinner />
            </div>
          </td>
        </tr>
      );
    } else if (
      state.status === TableStatus.Fail ||
      state.status === TableStatus.Error
    ) {
      body.push(
        <tr key="error-row">
          <td colSpan={columns.length} className={css.noData}>
            {state.error !== undefined
              ? state.error!.message
              : 'An error occurred and the data could not be loaded.'}
          </td>
        </tr>
      );
    } else {
      if (rows.length === 0) {
        body.push(
          <tr key="empty-row">
            <td colSpan={columns.length} className={css.noData}>
              <div className={css.emptyState}>
                <span className={css.emptyIcon}>📋</span>
                <p>No data to display</p>
                {activeFilters > 0 && (
                  <button 
                    className={css.clearFiltersButton}
                    onClick={() => onFilter && onFilter([])}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </td>
          </tr>
        );
      } else {
        rows.forEach((r, i) => {
          const cells: JSX.Element[] = [];

          columns.forEach((c) => {
            let cellClass = '';
            if (c.align === DisplayAlignment.Center) cellClass = css.center;
            if (c.align === DisplayAlignment.Right) cellClass = css.right;

            // Add hideOnMobile class for specific columns
            if (c.id === 'createdDate' || c.id === 'lastLoginDate' || c.id === 'enabled' || c.id === 'role') {
              cellClass = `${cellClass} ${css.hideOnMobile}`.trim();
            }

            // Use custom action renderer if provided and this is the actions column
            if (renderActionIcons && c instanceof ActionsColumn) {
              cells.push(
                <td className={`${css.tableCell} ${cellClass}`} key={c.id} data-column={c.id}>
                  {renderActionIcons(r)}
                </td>
              );
            } else {
              cells.push(
                <td className={`${css.tableCell} ${cellClass}`} key={c.id} data-column={c.id}>
                  {c.render(r, handleActionClick)}
                </td>
              );
            }
          });

          if (cells.length > 0) {
            body.push(<tr className={css.tableRow} key={`row-${i}-${r.id}`}>{cells}</tr>);
          }
        });
      }
    }

    return <tbody>{body}</tbody>;
  };

  const buildTableFooter = (): JSX.Element => {
    if (
      state.status === TableStatus.Acting ||
      state.status === TableStatus.Loading ||
      rows.length === 0
    ) {
      return <></>;
    }

    return (
      <div className={css.tableFooter}>
        <span>Showing 1 - {rows.length} of {total}</span>
        {onLoadMore && rows.length < total && (
          <button 
            className={css.loadMoreButton}
            onClick={onLoadMore}
            disabled={(state.status as TableStatus) === TableStatus.Loading}
          >
            {(state.status as TableStatus) === TableStatus.Loading 
              ? <><AiOutlineLoading3Quarters className={`${css.loadingIcon} ${css.spin}`} /> Loading...</>
              : 'Load More'
            }
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={css.tableContainer} data-table-state={JSON.stringify(state)}>
      {renderTableHeader()}
      
      <div className={css.tableContent}>
        <table className={css.table}>
          {buildTableHead()}
          {buildTableBody()}
        </table>
      </div>
      
      {buildTableFooter()}
    </div>
  );
};

// click wrapper
type ClickWrapperProps<R extends DataTableRow> = {
  action?: string;
  row: R;
  allowAction?: IsActionable<R>;
  handleAction: ActionHandler<R>;
  children: React.ReactNode;
};

const ClickWrapper = <R extends DataTableRow>({
  action,
  row,
  allowAction,
  handleAction,
  children,
}: ClickWrapperProps<R>) => {
  let onValueClick: React.MouseEventHandler | undefined = undefined;
  let valueClass = '';
  
  if (
    action !== undefined &&
    (allowAction === undefined || allowAction!(row))
  ) {
    onValueClick = () => {
      handleAction(action!, row);
    };
    valueClass = css.actionable;
  }

  return (
    <span className={valueClass} onClick={onValueClick}>
      {children}
    </span>
  );
};

export default AdminDataTable;  